import { useState, useCallback, useEffect } from "react";
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import {
  LayoutGrid,
  Table2,
  CalendarDays,
  CalendarRange,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ViewToggleTabs } from "@/components/navigation/view-toggle-tabs";
import WorkOrderModal from "@/components/operations/work-orders/work-order-modal";
import { Badge } from "@/components/ui/badge";

type SearchParams = {
  action?: "create";
};

export const Route = createFileRoute(
  "/_authenticated/operations/work-orders/_list",
)({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    action: search.action === "create" ? "create" : undefined,
  }),
  component: WorkOrdersListLayout,
});

const VIEW_OPTIONS = [
  { value: "kanban" as const, label: "Kanban", icon: LayoutGrid },
  { value: "table" as const, label: "Tabla", icon: Table2 },
  { value: "calendar" as const, label: "Mes", icon: CalendarDays },
  { value: "week" as const, label: "Semana", icon: CalendarRange },
];

type ViewKey = (typeof VIEW_OPTIONS)[number]["value"];

const VIEW_PATHS: Record<ViewKey, string> = {
  kanban: "/operations/work-orders",
  table: "/operations/work-orders/table",
  calendar: "/operations/work-orders/calendar",
  week: "/operations/work-orders/week",
};

const PATH_TO_VIEW: Record<string, ViewKey> = {
  "/operations/work-orders": "kanban",
  "/operations/work-orders/": "kanban",
  "/operations/work-orders/table": "table",
  "/operations/work-orders/calendar": "calendar",
  "/operations/work-orders/week": "week",
};

function WorkOrdersListLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { action } = useSearch({ from: Route.id });
  const [modalOpen, setModalOpen] = useState(false);
  const currentView = PATH_TO_VIEW[location.pathname] ?? "kanban";

  // Open modal if action=create in URL
  useEffect(() => {
    if (action === "create") {
      setModalOpen(true);
      // Clear the action from URL
      navigate({
        to: location.pathname as any,
        replace: true,
      });
    }
  }, [action, navigate, location.pathname]);

  const handleViewChange = useCallback(
    (view: ViewKey) => {
      navigate({ to: VIEW_PATHS[view] });
    },
    [navigate],
  );

  const handleCreateClick = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  // Atajo de teclado: "n" abre el modal de nueva orden en cualquier vista
  // del modulo (kanban/tabla/calendario/semana). Espejo del shortcut que
  // muestran las bulk actions de las data tables.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key.toLowerCase() !== "n") return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // No disparar si el usuario esta escribiendo en un input, textarea o
      // cualquier elemento editable (incluye celdas [contenteditable] y los
      // NumericInput).
      const tag = target.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target.isContentEditable
      ) {
        return;
      }
      // No disparar si ya hay un modal abierto en pantalla (WorkOrderModal,
      // AlertDialog, etc.) — evita disparar "Nueva" mientras se confirma otra
      // cosa.
      if (document.querySelector('[role="dialog"]')) return;
      if (modalOpen) return;

      e.preventDefault();
      setModalOpen(true);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalOpen]);

  return (
    <div className="flex-1 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex items-center justify-between pb-1 flex-shrink-0">
        <ViewToggleTabs
          options={VIEW_OPTIONS}
          value={currentView}
          onChange={handleViewChange}
          size="default"
          showLabels="responsive"
          alignment="left"
          variant="default"
        />
        <Button
          size="sm"
          className="h-7 text-xs gap-1.5"
          onClick={handleCreateClick}
        >
          <Plus className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Nueva Orden</span>

        </Button>
      </div>
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <Outlet />
      </div>
      <WorkOrderModal isOpen={modalOpen} onClose={handleModalClose} />
    </div>
  );
}
